import React, { useEffect, useState, useRef } from "react";
import ShowroomItem from "../../components/showroomItem/shoroomItem";
import OperatorCard from "../../components/OperatorCard/operatorCard";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import { useLicense } from "../../context/licenseContext";
import Loading from "../../components/Loading/Loading";
import "./showroom.css";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import { emptyFilters, FILTER_SESSION_KEY } from "../../constants/filterDefaults";
import { countActiveFilters } from "../../utils/countActiveFilters";

function loadFiltersFromSession() {
    try {
        const raw = sessionStorage.getItem(FILTER_SESSION_KEY);
        return raw ? { ...emptyFilters, ...JSON.parse(raw) } : { ...emptyFilters };
    } catch {
        return { ...emptyFilters };
    } 
} 

function saveFiltersToSession(filters) {
    sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify(filters));
}

const Showroom = ({ userId, setHeaderTitle }) => {
    const navigate = useNavigate(); 
    const toast = useToast();
    const { currentLicense } = useLicense();
    const [loading, setLoading] = useState(true);
    const [canViewShowroom, setCanViewShowroom] = useState(true);
    const componentRef = useRef(null);

    const [appliedFilters, setAppliedFilters] = useState(() => loadFiltersFromSession());
    const [panelOpen, setPanelOpen] = useState(
        () => localStorage.getItem("showroom_panel_open") !== "false"
    );
    const [productsOrOperators, setProductsOrOperators] = useState(
        sessionStorage.getItem("showroom_product_or_operators") || "products"
    );

    const [data, setData] = useState(null);
    const [operators, setOperators] = useState([]);
    const [operatorColorCounts, setOperatorColorCounts] = useState();

    const fetchUserData = async () => {
        try {
            const {
                selectedTypes,
                selectedColors,
                selectedProductTypes,
                terpene_level_max,
                terpene_level_min,
                cannabinoid_level_max,
                cannabinoid_level_min,
                price_max,
                price_min,
                filterByProductName,
                filteredTerpenes,
                filteredCannabinoids,
                minTotalCannabinoids,
                maxTotalCannabinoids,
                minTotalTerpenes,
                maxTotalTerpenes,
            } = appliedFilters;

            const params = new URLSearchParams({
                states: currentLicense.state,
                selectedTypes: JSON.stringify(selectedTypes),
                industries: currentLicense.industry,
                colors: JSON.stringify(selectedColors),
                selectedProductTypes: JSON.stringify(selectedProductTypes),
                terpene_level_max,
                terpene_level_min,
                cannabinoid_level_max,
                cannabinoid_level_min,
                price_max,
                price_min,
                filterByProductName,
                filteredTerpenes: JSON.stringify(filteredTerpenes),
                filteredCannabinoids: JSON.stringify(filteredCannabinoids),
                minTotalCannabinoids,
                maxTotalCannabinoids,
                minTotalTerpenes,
                maxTotalTerpenes,
            });
            const showroomItems = await apiClient.get(`/showroom/get-showroom-items?${params.toString()}`);
            const showroomData = showroomItems.data;
            setData(showroomData);
            const uniqueLicenseNumbers = [...new Set(showroomData.map((item) => item.licenseNo))];
            const colorCounts = await apiClient.post(`/showroom/get-color-counts`, {
                licenseNo: uniqueLicenseNumbers,
            });
            setOperatorColorCounts(colorCounts.data);
            const operators = await Promise.all(
                uniqueLicenseNumbers.map((licenseNo) => apiClient.get(`/license/get-by-license/${licenseNo}`))
            );
            const operatorsData = operators.map((operator) => operator.data);
            setOperators(operatorsData);
        } catch (error) {
            console.error("Error fetching showroom data:", error);
            toast.error(error.userMessage || "Failed to load showroom data");
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => setCanViewShowroom(res.data?.can_view_showroom !== 0))
            .catch(() => setCanViewShowroom(true));
    }, [currentLicense]);

    useEffect(() => {
        setHeaderTitle("DYC Network");
    }, []);

    useEffect(() => {
        if (!currentLicense) return;
        fetchUserData();
    }, [appliedFilters, currentLicense]);

    const handleApplyFilters = (pendingFilters) => {
        saveFiltersToSession(pendingFilters);
        setAppliedFilters(pendingFilters);
    };

    const handlePanelToggle = () => {
        setPanelOpen((prev) => {
            const next = !prev;
            localStorage.setItem("showroom_panel_open", String(next));
            return next;
        });
    };

    if (!canViewShowroom) {
        return (
            <div className="showroom-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
                <h3>You don't have permission to view the showroom.</h3>
            </div>
        );
    }

    return (
        <div className="showroom-container" ref={componentRef}>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="showroom-toolbar">
                        <button
                            className={`showroom-toggle-btn${panelOpen ? " showroom-toggle-btn--open" : ""}`}
                            onClick={handlePanelToggle}
                            aria-label={panelOpen ? "Close filters" : "Open filters"}
                        >
                            <span className="showroom-toggle-icon">☰</span>
                            {panelOpen ? "Hide Filters" : "Filters"}
                            {!panelOpen && countActiveFilters(appliedFilters) > 0 && (
                                <span className="showroom-filter-badge">
                                    {countActiveFilters(appliedFilters)}
                                </span>
                            )}
                        </button>
                        {data && (
                            <span className="showroom-result-count">
                                {data.length} result{data.length !== 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                    <div className="showroom-wrapper">
                        <FilterPanel
                            isOpen={panelOpen}
                            filters={appliedFilters}
                            onFilterChange={handleApplyFilters}
                            productsOrOperators={productsOrOperators}
                            setProductsOrOperators={setProductsOrOperators}
                        />
                        <div className="showroom-list-container" data-tutorial="showroom-grid">
                            {data && data.length > 0 ? (
                                    <>
                                        {data.map((item, index) => (
                                            <ShowroomItem item={item} key={index} />
                                        ))}
                                    </>
                            ) : (
                                <div className="empty-container-showroom">
                                    {currentLicense.profile === "retailer" ? (
                                        <div className="no-orders-message">
                                            Unfortunately, no wholesalers have uploaded products yet. Please check back
                                            later.
                                        </div>
                                    ) : ( 
                                        <div className="no-orders-message">
                                            <span
                                                className="to-showroom-button"
                                                onClick={() => {
                                                    navigate("/Dashboard");
                                                }}
                                            >
                                                Be the first to upload your products!
                                            </span>{" "}
                                            Retailers will be able to see your products in the DYC Network
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Showroom;
